import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { posAPI, eventsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import LoadingSpinner from '../../components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;



// --- Styled Components ---

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  padding: 2rem;
  
  @media (min-width: 1024px) {
    padding-top: 110px;
  }
  background-color: #050505;
  color: #e5edff;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

  /* Dynamic Mesh Background */
  background-image: 
    radial-gradient(at 0% 0%, rgba(41, 151, 255, 0.15) 0px, transparent 50%),
    radial-gradient(at 100% 0%, rgba(255, 41, 105, 0.1) 0px, transparent 50%),
    radial-gradient(at 100% 100%, rgba(0, 224, 255, 0.12) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(41, 151, 255, 0.08) 0px, transparent 50%);
  background-attachment: fixed;
`;

const ContentGrid = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  position: relative;
  z-index: 10;
  animation: ${fadeIn} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const GlassPanel = styled.div`
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const Header = styled(GlassPanel)`
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(20, 20, 25, 0.4);
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BackLink = styled(Link)`
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
  letter-spacing: -0.01em;
`;

const MetaTags = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-top: 0.2rem;
`;

const Tag = styled.span`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.06);
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const TicketGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
`;

const TicketCard = styled.button`
  position: relative;
  background: ${props => props.selected
    ? 'linear-gradient(135deg, rgba(41, 151, 255, 0.2), rgba(41, 151, 255, 0.1))'
    : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.selected
    ? '#2997ff'
    : 'rgba(255, 255, 255, 0.08)'};
  padding: 1.25rem;
  border-radius: 16px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  outline: none;

  &:hover {
    background: ${props => props.selected
    ? 'linear-gradient(135deg, rgba(41, 151, 255, 0.25), rgba(41, 151, 255, 0.15))'
    : 'rgba(255, 255, 255, 0.06)'};
    transform: translateY(-2px);
  }

  ${props => props.selected && css`
    box-shadow: 0 0 0 4px rgba(41, 151, 255, 0.15);
  `}
`;

const TicketName = styled.div`
  font-weight: 600;
  font-size: 1.05rem;
  color: #fff;
`;

const TicketPrice = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #2997ff;
`;

const TicketMeta = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
`;

const FormPanel = styled(GlassPanel)`
  padding: 2rem;
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.6rem;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  color: #fff;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2997ff;
    box-shadow: 0 0 0 4px rgba(41, 151, 255, 0.1);
    background: rgba(0, 0, 0, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #2997ff, #0066cc);
  color: #fff;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(41, 151, 255, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(41, 151, 255, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.1);
    box-shadow: none;
    color: rgba(255, 255, 255, 0.3);
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoCard = styled(GlassPanel)`
  padding: 1.5rem;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  span:first-child {
    color: rgba(255, 255, 255, 0.6);
  }

  span:last-child {
    color: #fff;
    font-weight: 600;
  }
`;

const Highlight = styled.span`
  color: ${props => props.danger ? '#ff3b30' : '#32d74b'} !important;
`;

const SuccessState = styled(GlassPanel)`
  padding: 3rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  background: rgba(50, 215, 75, 0.1);
  border-color: rgba(50, 215, 75, 0.2);
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #32d74b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 2rem;
  box-shadow: 0 0 30px rgba(50, 215, 75, 0.4);
`;

const PosTerminalPage = () => {
  const { eventId } = useParams();
  const { user } = useAuthStore();

  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [quantities, setQuantities] = useState({});
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [eventData, statsData, ticketTypesData] = await Promise.all([
          eventsAPI.getById(eventId),
          posAPI.getAgentStats(user.id, eventId),
          eventsAPI.getTicketTypes(eventId)
        ]);

        setEvent(eventData);
        setStats(statsData);
        setTicketTypes(ticketTypesData);
        const initialQuantities = {};
        ticketTypesData.forEach(tt => {
          initialQuantities[tt.id] = 0;
        });
        setQuantities(initialQuantities);
      } catch (err) {
        console.error('Error loading POS data:', err);
        toast.error('Failed to load event data');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id && eventId) {
      fetchData();
    }
  }, [user, eventId]);

  const handleQuantityChange = (ticketTypeId, value) => {
    const parsed = Math.max(0, parseInt(value || '0', 10));
    setQuantities(prev => ({ ...prev, [ticketTypeId]: parsed }));
  };

  const handleSell = async (e) => {
    e.preventDefault();
    const items = Object.entries(quantities || {})
      .filter(([, qty]) => qty > 0)
      .map(([ticketTypeId, qty]) => ({
        ticketTypeId,
        quantity: qty
      }));

    if (!items.length || !customerEmail) {
      toast.error('Select at least one ticket and enter customer email');
      return;
    }

    setProcessing(true);
    try {
      const order = await posAPI.sellTicket({
        agentId: user.id,
        eventId,
        items,
        customerEmail,
        customerName
      });

      setLastOrder(order);
      toast.success('Ticket sold successfully!');

      // Reset form
      setCustomerEmail('');
      setCustomerName('');
      const resetQuantities = {};
      ticketTypes.forEach(tt => {
        resetQuantities[tt.id] = 0;
      });
      setQuantities(resetQuantities);

      // Refresh stats
      const newStats = await posAPI.getAgentStats(user.id, eventId);
      setStats(newStats);

    } catch (err) {
      console.error('Sale error:', err);
      toast.error(err.message || 'Failed to process sale');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!event) return <div>Event not found</div>;

  return (
    <Page>
      <ContentGrid>
        <MainSection>
          <Header>
            <HeaderLeft>
              <BackLink to="/pos">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
              </BackLink>
              <Title>{event.title}</Title>
              <MetaTags>
                <Tag>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {event.startTime ? format(new Date(event.startTime), 'MMM d, yyyy h:mm a') : 'TBA'}
                </Tag>
                <Tag>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {event.venue?.name || event.venueName || 'TBA'}
                </Tag>
              </MetaTags>
            </HeaderLeft>
          </Header>

          {lastOrder ? (
            <SuccessState>
              <SuccessIcon>✓</SuccessIcon>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', color: '#fff' }}>Sale Complete!</h2>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)' }}>
                  Order #{lastOrder.orderNumber} sent to {lastOrder.customerEmail}
                </p>
              </div>
              <PrimaryButton
                onClick={() => setLastOrder(null)}
                style={{ maxWidth: '200px', marginTop: '1rem' }}
              >
                Start New Sale
              </PrimaryButton>
            </SuccessState>
          ) : (
            <FormPanel>
              <form onSubmit={handleSell}>
                <SectionTitle>1. Select Tickets</SectionTitle>
                <TicketGrid>
                  {ticketTypes.map(tt => (
                    <TicketCard
                      key={tt.id}
                      type="button"
                      selected={(quantities[tt.id] || 0) > 0}
                      onClick={() => handleQuantityChange(tt.id, (quantities[tt.id] || 0) + 1)}
                    >
                      <TicketName>{tt.name}</TicketName>
                      <TicketPrice>
                        {tt.priceAmount > 0 ? `${tt.currencyCode} ${tt.priceAmount}` : 'Free'}
                      </TicketPrice>
                      <TicketMeta>
                        {tt.capacity ? `${tt.capacity} available` : 'General Admission'}
                      </TicketMeta>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          marginTop: '0.6rem',
                          background: 'rgba(255,255,255,0.04)',
                          padding: '0.35rem 0.5rem',
                          borderRadius: '999px',
                          border: '1px solid rgba(255,255,255,0.06)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(tt.id, (quantities[tt.id] || 0) - 1)}
                          disabled={(quantities[tt.id] || 0) <= 0 || processing}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: '1px solid rgba(255,255,255,0.2)',
                            background: 'rgba(255,255,255,0.06)',
                            color: '#fff',
                            cursor: (quantities[tt.id] || 0) <= 0 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          –
                        </button>
                        <span style={{ minWidth: '24px', textAlign: 'center', color: '#fff', fontWeight: 700 }}>
                          {quantities[tt.id] || 0}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(tt.id, (quantities[tt.id] || 0) + 1)}
                          disabled={processing}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: '1px solid #2997ff',
                            background: 'linear-gradient(135deg, #2997ff, #2ed5ff)',
                            color: '#000',
                            fontWeight: 700,
                            cursor: processing ? 'not-allowed' : 'pointer'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </TicketCard>
                  ))}
                </TicketGrid>

                <div style={{ height: '2rem' }} />

                <SectionTitle>2. Customer Details</SectionTitle>
                <InputGroup>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    required
                    value={customerEmail}
                    onChange={e => setCustomerEmail(e.target.value)}
                    placeholder="customer@example.com"
                  />
                </InputGroup>
                <InputGroup>
                  <Label>Full Name (Optional)</Label>
                  <Input
                    type="text"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                    placeholder="John Doe"
                  />
                </InputGroup>

                <PrimaryButton
                  type="submit"
                  disabled={
                    processing ||
                    !customerEmail ||
                    !Object.values(quantities || {}).some(qty => qty > 0)
                  }
                >
                  {processing ? 'Processing Sale...' : 'Complete Sale'}
                </PrimaryButton>
              </form>
            </FormPanel>
          )}
        </MainSection>

        <Sidebar>
          <InfoCard>
            <SectionTitle>Agent Stats</SectionTitle>
            {stats && (
              <>
                <StatRow>
                  <span>Allocation Limit</span>
                  <span>{stats.limit > 2000000000 ? 'Unlimited' : stats.limit}</span>
                </StatRow>
                <StatRow>
                  <span>Tickets Sold</span>
                  <span>{stats.sold}</span>
                </StatRow>
                <StatRow>
                  <span>Remaining</span>
                  <Highlight danger={stats.remaining < 10}>
                    {stats.remaining}
                  </Highlight>
                </StatRow>
              </>
            )}
          </InfoCard>

          <InfoCard style={{ background: 'rgba(41, 151, 255, 0.05)', borderColor: 'rgba(41, 151, 255, 0.1)' }}>
            <SectionTitle style={{ color: '#2997ff' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              Instructions
            </SectionTitle>
            <ul style={{
              margin: 0,
              paddingLeft: '1.2rem',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}>
              <li>Verify customer identity if required.</li>
              <li>Collect payment via external terminal.</li>
              <li>Tickets are emailed immediately.</li>
            </ul>
          </InfoCard>
        </Sidebar>
      </ContentGrid>
    </Page>
  );
};

export default PosTerminalPage;
